import React from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";

// --- IMAGE IMPORTS ---
import educationImg from "../assets/images/2864oCA9gAQQDedTx6ZADR2q8682_images_A group of studets doing a project and a guide mentoring them_ink-drawing-v3_0_1756570277921_XJUUe4_0_generation.png";
import projectImg from "../assets/images/project_guide.png";
import startupImg from "../assets/images/startup_conslt.png";
import roboticsImg from "../assets/images/robot.png";
import customImg from "../assets/images/education_train.png";

function FuturisticBackground() {
  const { scrollY } = useScroll();

  const yGrid = useTransform(scrollY, [0, 1000], [0, -150]);
  const rotateGrid = useTransform(scrollY, [0, 1000], [0, 5]);
  const yOrb2 = useTransform(scrollY, [0, 1000], [0, -200]);
  const rotateRing = useTransform(scrollY, [0, 2000], [0, 360]);
  const smoothRotate = useSpring(rotateRing, { stiffness: 50, damping: 20 });

  return (
    <div className="fixed inset-0 overflow-hidden -z-10 bg-[#003049]">
      <div className="absolute inset-0 bg-gradient-to-br from-[#001824] via-[#003049] to-[#004e75]" />
      <motion.div 
        style={{ y: yGrid, rotateX: rotateGrid }}
        className="absolute inset-[-50%] w-[200%] h-[200%] origin-top bg-[linear-gradient(to_right,#457B9D22_1px,transparent_1px),linear-gradient(to_bottom,#457B9D22_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_110%)] perspective-1000" 
      />
      <motion.div 
        style={{ y: yOrb2 }}
        className="absolute bottom-[10%] right-[5%] w-[40vw] h-[40vw] bg-[#457B9D] rounded-full blur-[140px] opacity-30 mix-blend-screen"
      />
      <motion.div
        style={{ rotate: smoothRotate }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] border border-[#D1D1D1]/10 rounded-full border-dashed pointer-events-none"
      />
      {[...Array(30)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-1 h-1 bg-[#D1D1D1] rounded-full"
          initial={{
            x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1000),
            y: Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 1000),
            scale: Math.random() * 2,
            opacity: Math.random() * 0.5 + 0.3
          }}
          animate={{
            y: [null, Math.random() * -150],
            opacity: [null, 0.1, 0.6, 0.1],
          }}
          transition={{
            duration: Math.random() * 15 + 15,
            repeat: Infinity,
            ease: "linear",
          }}
        />
      ))}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[500px] bg-gradient-to-b from-[#457B9D]/40 to-transparent blur-[80px] pointer-events-none" />
    </div>
  );
}

function ServicesPage() {
  const services = [
    { title: "Education and Training", desc: "Hands-on robotics courses for schools, colleges, and professionals.", img: educationImg },
    { title: "Project Guidance and Mentorship", desc: "Personalized support to help you succeed in robotics competitions and projects.", img: projectImg },
    { title: "Startup Consultation", desc: "Expert advice to launch and grow your startup.", img: startupImg },
    { title: "Robotics Consultation", desc: "Tailored solutions to optimize your robotics initiatives.", img: roboticsImg },
    { title: "Custom Product Development", desc: "Bespoke robotics solutions to meet your unique needs.", img: customImg }
  ];

  return (
    <div className="font-sans min-h-screen pt-24 px-4 pb-12 text-gray-200 relative overflow-hidden selection:bg-[#D1D1D1] selection:text-[#003049]">
      <FuturisticBackground />

      <motion.div 
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        className="relative z-10"
      >
        <h1 className="text-4xl md:text-5xl font-bold text-center mb-4 text-white">
          Our <span className="text-[#D1D1D1]">Services</span>
        </h1>
        <p className="text-center text-gray-300 mb-12 max-w-2xl mx-auto text-lg">
          Explore our professional robotics services designed to help your business innovate and excel.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              viewport={{ once: true }}
              className="relative rounded-xl overflow-hidden shadow-xl bg-[#002639] group border border-[#457B9D] hover:border-[#D1D1D1] transition-colors duration-300"
            >
              <div className="relative h-64 overflow-hidden">
                <img
                  src={service.img}
                  alt={service.title}
                  className="w-full h-full object-cover opacity-70 group-hover:opacity-100 group-hover:scale-110 transition-all duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#002639] via-[#002639]/60 to-transparent" />
              </div>
              
              <div className="absolute inset-0 p-6 flex flex-col justify-end">
                <h2 className="text-white font-bold text-2xl mb-2 group-hover:text-[#D1D1D1] transition-colors duration-300 drop-shadow-md">
                  {service.title}
                </h2>
                <p className="text-gray-300 mb-2 group-hover:text-white transition-colors duration-300">
                  {service.desc}
                </p>
                <div className="w-12 h-1 bg-[#D1D1D1] rounded-full transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}

export default ServicesPage;
